<template>
  <v-container fluid class="loan-stepper-app">
    <!-- Cosmic Background -->
    <div class="cosmic-bg">
      <div class="cosmic-orb orb-1"></div>
      <div class="cosmic-orb orb-2"></div>
      <div class="cosmic-orb orb-3"></div>
    </div>

    <!-- ══════════════ LOADING STATE WITH ANIMATION ══════════════ -->
    <v-card v-if="loading" class="loading-card elevation-8">
      <div class="loading-content">
        <!-- Animated Eligibility Check -->
        <div class="eligibility-animation-wrapper">
          <div class="scanning-circle">
            <div class="scan-line"></div>
          </div>
          <v-icon size="64" color="#00479b" class="check-icon">account_circle</v-icon>
        </div>

        <h2 class="loading-title mt-8">Analyzing your profile...</h2>
        <p class="loading-subtitle">Please wait while we verify your eligibility</p>

        <!-- Progress Steps Animation -->
        <div class="check-steps mt-8">
          <div
            v-for="(step, idx) in checkSteps"
            :key="idx"
            class="check-step"
            :class="{ 'step-active': currentCheckStep >= idx, 'step-complete': currentCheckStep > idx }"
          >
            <div class="step-icon-wrapper">
              <v-icon v-if="currentCheckStep > idx" color="success" small>check_circle</v-icon>
              <div v-else-if="currentCheckStep === idx" class="step-spinner"></div>
              <v-icon v-else color="grey" small>radio_button_unchecked</v-icon>
            </div>
            <span class="step-text">{{ step }}</span>
          </div>
        </div>
      </div>
    </v-card>

    <!-- ══════════════ ACTIVE LOAN FIRST ══════════════ -->
    <v-card v-else-if="eligibility.has_active_loan" class="active-loan-block-card elevation-8">
      <div class="active-loan-header">
        <v-icon size="96" color="white">hourglass_empty</v-icon>
      </div>

      <v-card-text class="pa-8">
        <h2 class="active-loan-title">Complete your current loan first</h2>
        <p class="active-loan-desc">Good news! You're eligible for loans, but you need to finish repaying your active loan before applying for a new one.</p>

        <!-- Active Loan Details Card -->
        <v-card class="active-loan-details mt-8" outlined>
          <v-card-title class="pb-2">
            <v-icon left color="#00479b">account_balance</v-icon>
            Your active loan
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <div class="loan-detail-row">
              <span>Loan ID</span>
              <span class="font-weight-bold">#{{ eligibility.active_loan_details.loan_id }}</span>
            </div>
            <div class="loan-detail-row">
              <span>Loan amount</span>
              <span class="font-weight-bold">{{ formatAmount(eligibility.active_loan_details.loan_amount) }} FCFA</span>
            </div>
            <div class="loan-detail-row">
              <span>Total to repay</span>
              <span class="font-weight-bold" style="color: #00479b">{{ formatAmount(eligibility.active_loan_details.total_amount) }} FCFA</span>
            </div>
            <div class="loan-detail-row">
              <span>Already paid</span>
              <span class="font-weight-bold text-success">{{ formatAmount(eligibility.active_loan_details.amount_paid) }} FCFA</span>
            </div>
            <div class="loan-detail-row highlight">
              <span>Remaining</span>
              <span class="font-weight-bold orange--text" style="font-size: 1.2rem">{{ formatAmount(eligibility.active_loan_details.remaining_amount) }} FCFA</span>
            </div>
            <div class="loan-detail-row">
              <span>Due date</span>
              <span class="font-weight-bold">{{ formatDateLoan(eligibility.active_loan_details.due_date) }}</span>
            </div>
            <div class="loan-detail-row">
              <span>Status</span>
              <v-chip small :color="getStatusColor(eligibility.active_loan_details.status)" text-color="white">
                {{ eligibility.active_loan_details.status }}
              </v-chip>
            </div>

            <!-- Progress Bar -->
            <div class="mt-6">
              <div class="progress-label mb-2">
                Repayment progress: {{ getRepaymentProgress() }}%
              </div>
              <v-progress-linear
                :value="getRepaymentProgress()"
                color="success"
                height="8"
                rounded
              ></v-progress-linear>
            </div>
          </v-card-text>
        </v-card>

        <!-- Your Future Eligibility Info -->
        <v-card class="future-eligibility-card mt-6" color="blue lighten-5" outlined>
          <v-card-text class="pa-6">
            <div class="d-flex align-center mb-4">
              <v-icon color="success" size="32" class="mr-3">verified</v-icon>
              <div>
                <div class="font-weight-bold" style="font-size: 1.1rem">You're eligible!</div>
                <div class="caption">Once you complete your current loan</div>
              </div>
            </div>

            <v-divider class="mb-4"></v-divider>

            <div class="eligibility-info-row">
              <span>Maximum loan available:</span>
              <span class="font-weight-bold success--text" style="font-size: 1.2rem">
                {{ formatAmount(eligibility.max_amount) }} FCFA
              </span>
            </div>

            <div class="mt-4">
              <div class="caption mb-2">Criteria met:</div>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="(c, i) in eligibility.criteria_met"
                  :key="i"
                  x-small
                  color="success"
                  text-color="white"
                  class="ma-1"
                >
                  <v-icon x-small left>check</v-icon>
                  {{ formatCriterion(c) }}
                </v-chip>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Action Buttons -->
        <div class="mt-8">
          <v-btn
            color="#00479b"
            dark
            large
            block
            :to="`/savings/loans/detail/${eligibility.active_loan_details.loan_id}`"
            class="mb-3"
          >
            <v-icon left>visibility</v-icon>
            View loan details
          </v-btn>

          <v-btn
            text
            large
            block
            color="success"
            @click="showAllCriteria = true"
          >
            <v-icon left>assessment</v-icon>
            View my eligibility details
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- ══════════════ NOT ELIGIBLE STATE ══════════════ -->
    <v-card v-else-if="!eligibility.is_eligible" class="not-eligible-card elevation-8">
      <div class="not-eligible-header">
        <v-icon size="96" color="white">lock</v-icon>
      </div>

      <v-card-text class="pa-8">
        <h2 class="not-eligible-title">Not eligible yet</h2>
        <p class="not-eligible-desc">You need to meet at least ONE of the criteria below to access loans.</p>

        <!-- Criteria Status Cards -->
        <div class="criteria-status-grid mt-8">
          <v-card
            v-for="(criterion, idx) in allCriteria"
            :key="idx"
            class="criterion-status-card"
            :class="{ 'criterion-met': criterion.met, 'criterion-unmet': !criterion.met }"
            outlined
          >
            <div class="criterion-icon-wrapper" :class="{ 'icon-success': criterion.met }">
              <v-icon :color="criterion.met ? 'success' : 'grey'" size="40">
                {{ criterion.met ? 'check_circle' : 'cancel' }}
              </v-icon>
            </div>
            <div class="criterion-content">
              <div class="criterion-name">{{ criterion.name }}</div>
              <div class="criterion-status-text" :class="{ 'text-success': criterion.met }">
                {{ criterion.statusText }}
              </div>
            </div>
            <v-btn
              icon
              small
              color="#00479b"
              @click="showCriterionDetail(idx)"
              class="criterion-info-btn"
            >
              <v-icon small>info</v-icon>
            </v-btn>
          </v-card>
        </div>

        <v-btn
          color="#00479b"
          dark
          large
          block
          class="mt-8"
          @click="showEligibilityExplanation = true"
        >
          <v-icon left>school</v-icon>
          Learn more about eligibility
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- ══════════════ ELIGIBLE STATE (NO ACTIVE LOAN) ══════════════ -->
    <div v-else class="eligible-section">
      <!-- Success Banner -->
      <v-card class="success-banner elevation-4 mb-8">
        <v-card-text class="pa-6">
          <div class="success-banner-content">
            <div class="success-icon-wrapper">
              <v-icon color="white" size="56">verified</v-icon>
            </div>
            <div class="success-text">
              <div class="success-title">🎉 Congratulations!</div>
              <div class="success-subtitle">You are eligible to apply for a loan</div>
            </div>
          </div>

          <!-- Met Criteria -->
          <div class="met-criteria mt-6">
            <div class="criteria-label">✓ Criteria met:</div>
            <div class="criteria-chips">
              <v-chip
                v-for="(c, i) in eligibility.criteria_met"
                :key="i"
                small
                color="success"
                text-color="white"
                class="ma-1"
              >
                <v-icon x-small left>check</v-icon>
                {{ formatCriterion(c) }}
              </v-chip>
            </div>
          </div>

          <!-- View All Criteria Button -->
          <v-btn
            text
            small
            color="success"
            class="mt-4"
            @click="showAllCriteria = true"
          >
            <v-icon small left>visibility</v-icon>
            View all criteria status
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- ══════════════ MOBILE CUSTOM STEPPER ══════════════ -->
      <div class="mobile-stepper-wrapper">
        <!-- Progress Bar -->
        <div class="mobile-progress-container">
          <div class="mobile-progress-bar">
            <div class="mobile-progress-fill" :style="{ width: progressPercent + '%' }"></div>
          </div>
          <div class="mobile-step-indicator">Step {{ currentStep }} of 5</div>
        </div>

        <!-- Steps Dots -->
        <div class="mobile-steps-dots">
          <div
            v-for="step in 5"
            :key="step"
            class="mobile-dot"
            :class="{
              'dot-active': step === currentStep,
              'dot-completed': step < currentStep
            }"
            @click="jumpToStep(step)"
          >
            <v-icon v-if="step < currentStep" size="16" color="white">check</v-icon>
            <span v-else>{{ step }}</span>
          </div>
        </div>

        <!-- Step Names -->
        <div class="mobile-step-name">{{ stepNames[currentStep - 1] }}</div>

        <!-- Step Content -->
        <v-card class="mobile-step-card elevation-4">
          <!-- Step 1: Amount -->
          <div v-if="currentStep === 1" class="mobile-step-content">
            <div class="mobile-step-header">
              <v-icon large color="#00479b">monetization_on</v-icon>
              <h3>Choose amount</h3>
            </div>

            <div class="mobile-amounts-grid">
              <div
                v-for="amount in availableAmounts"
                :key="amount"
                class="mobile-amount-card"
                :class="{ 'mobile-amount-selected': loanApplication.loan_amount === amount }"
                @click="selectAmount(amount)"
              >
                <div class="mobile-amount-value">{{ formatAmount(amount) }}</div>
                <div class="mobile-amount-unit">FCFA</div>
                <v-icon
                  v-if="loanApplication.loan_amount === amount"
                  color="success"
                  class="mobile-amount-check"
                  size="20"
                >
                  check_circle
                </v-icon>
              </div>
            </div>

            <v-btn
              color="#00479b"
              dark
              large
              block
              :disabled="!loanApplication.loan_amount"
              @click="nextStep"
              class="mobile-next-btn"
            >
              Continue
              <v-icon right>arrow_forward</v-icon>
            </v-btn>
          </div>

          <!-- Step 2: Period -->
          <div v-else-if="currentStep === 2" class="mobile-step-content">
            <div class="mobile-step-header">
              <v-icon large color="#00479b">schedule</v-icon>
              <h3>Repayment period</h3>
            </div>

            <div class="mobile-period-options">
              <div
                class="mobile-period-card"
                :class="{ 'mobile-period-selected': loanApplication.repayment_period_days === 15 }"
                @click="selectPeriod(15)"
              >
                <v-icon size="56" :color="loanApplication.repayment_period_days === 15 ? '#00479b' : 'grey'">
                  flash_on
                </v-icon>
                <div class="mobile-period-days">15 Days</div>
                <div class="mobile-period-label">Short term</div>
                <div v-if="loanApplication.loan_amount" class="mobile-period-interest">
                  Interest: {{ formatAmount(getInterest(15)) }} FCFA
                </div>
                <v-icon
                  v-if="loanApplication.repayment_period_days === 15"
                  color="success"
                  class="mobile-period-check"
                >
                  check_circle
                </v-icon>
              </div>

              <div
                class="mobile-period-card"
                :class="{ 'mobile-period-selected': loanApplication.repayment_period_days === 25 }"
                @click="selectPeriod(25)"
              >
                <v-icon size="56" :color="loanApplication.repayment_period_days === 25 ? '#00479b' : 'grey'">
                  event
                </v-icon>
                <div class="mobile-period-days">25 Days</div>
                <div class="mobile-period-label">Medium term</div>
                <div v-if="loanApplication.loan_amount" class="mobile-period-interest">
                  Interest: {{ formatAmount(getInterest(25)) }} FCFA
                </div>
                <v-icon
                  v-if="loanApplication.repayment_period_days === 25"
                  color="success"
                  class="mobile-period-check"
                >
                  check_circle
                </v-icon>
              </div>
            </div>

            <div class="mobile-step-actions">
              <v-btn text large @click="prevStep" class="mobile-back-btn">
                <v-icon left>arrow_back</v-icon>
                Back
              </v-btn>
              <v-btn color="#00479b" dark large @click="nextStep" class="mobile-next-btn">
                Continue
                <v-icon right>arrow_forward</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Step 3: Payment -->
          <div v-else-if="currentStep === 3" class="mobile-step-content">
            <div class="mobile-step-header">
              <v-icon large color="#00479b">phone_android</v-icon>
              <h3>Mobile Money</h3>
            </div>

            <v-text-field
              v-model="loanApplication.phone_number"
              label="Phone number"
              outlined
              prepend-inner-icon="phone"
              :rules="phoneRules"
              type="tel"
              class="mb-4"
            ></v-text-field>

            <div class="mobile-operator-label">Select operator</div>
            <div class="mobile-operator-options">
              <div
                class="mobile-operator-card"
                :class="{ 'mobile-operator-selected': loanApplication.operator === 'MTN' }"
                @click="selectOperator('MTN')"
              >
                <v-img
                  :src="require('@/assets/mtn-mobile-money-logo.png')"
                  contain
                  height="60"
                ></v-img>
                <div class="mobile-operator-name">MTN</div>
                <v-icon
                  v-if="loanApplication.operator === 'MTN'"
                  color="success"
                  class="mobile-operator-check"
                >
                  check_circle
                </v-icon>
              </div>

              <div
                class="mobile-operator-card"
                :class="{ 'mobile-operator-selected': loanApplication.operator === 'OM' }"
                @click="selectOperator('OM')"
              >
                <v-img
                  :src="require('@/assets/om.png')"
                  contain
                  height="60"
                ></v-img>
                <div class="mobile-operator-name">Orange</div>
                <v-icon
                  v-if="loanApplication.operator === 'OM'"
                  color="success"
                  class="mobile-operator-check"
                >
                  check_circle
                </v-icon>
              </div>
            </div>

            <div class="mobile-step-actions">
              <v-btn text large @click="prevStep" class="mobile-back-btn">
                <v-icon left>arrow_back</v-icon>
                Back
              </v-btn>
              <v-btn
                color="#00479b"
                dark
                large
                :disabled="!loanApplication.phone_number || !loanApplication.operator"
                @click="nextStep"
                class="mobile-next-btn"
              >
                Continue
                <v-icon right>arrow_forward</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Step 4: Purpose -->
          <div v-else-if="currentStep === 4" class="mobile-step-content">
            <div class="mobile-step-header">
              <v-icon large color="#00479b">description</v-icon>
              <h3>Loan purpose</h3>
              <p class="mobile-step-subtitle">(Optional)</p>
            </div>

            <v-textarea
              v-model="loanApplication.purpose"
              label="Describe your need..."
              outlined
              rows="5"
              counter="500"
              :rules="purposeRules"
              class="mobile-purpose-textarea"
            ></v-textarea>

            <div class="mobile-step-actions">
              <v-btn text large @click="prevStep" class="mobile-back-btn">
                <v-icon left>arrow_back</v-icon>
                Back
              </v-btn>
              <v-btn color="#00479b" dark large @click="nextStep" class="mobile-next-btn">
                Continue
                <v-icon right>arrow_forward</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Step 5: Confirmation -->
          <div v-else-if="currentStep === 5" class="mobile-step-content">
            <div class="mobile-step-header">
              <v-icon large color="#00479b">check_circle</v-icon>
              <h3>Confirm loan</h3>
            </div>

            <div class="mobile-summary">
              <div class="mobile-summary-row">
                <span>Amount</span>
                <span class="value">{{ formatAmount(loanApplication.loan_amount) }}</span>
              </div>
              <div class="mobile-summary-row">
                <span>Interest ({{ interestRatePct }})</span>
                <span class="value orange--text">{{ formatAmount(currentInterest) }}</span>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="mobile-summary-row total">
                <span>Total to repay</span>
                <span class="value">{{ formatAmount(totalRepayment) }}</span>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="mobile-summary-row">
                <span>Period</span>
                <span class="value">{{ loanApplication.repayment_period_days }} days</span>
              </div>
              <div class="mobile-summary-row">
                <span>Due date</span>
                <span class="value">{{ dueDateDisplay }}</span>
              </div>
              <div class="mobile-summary-row">
                <span>Phone</span>
                <span class="value">{{ loanApplication.phone_number }}</span>
              </div>
              <div class="mobile-summary-row">
                <span>Operator</span>
                <span class="value">{{ loanApplication.operator }}</span>
              </div>
            </div>

            <v-checkbox
              v-model="agreeToTerms"
              :rules="termsRules"
              color="success"
              class="mt-4 mobile-terms-checkbox"
            >
              <template v-slot:label>
                <span class="mobile-terms-text">
                  I accept the
                  <a href="#" @click.stop.prevent="showTermsDialog = true" class="terms-link">terms</a>
                </span>
              </template>
            </v-checkbox>

            <div class="mobile-step-actions">
              <v-btn text large @click="prevStep" class="mobile-back-btn">
                <v-icon left>arrow_back</v-icon>
                Back
              </v-btn>
              <v-btn
                color="success"
                dark
                large
                :disabled="!agreeToTerms"
                :loading="submitting"
                @click="submitApplication"
                class="mobile-submit-btn"
              >
                <v-icon left>send</v-icon>
                Submit
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>
    </div>

    <!-- ══════════════ ALL CRITERIA DIALOG ENRICHI ══════════════ -->
    <v-dialog v-model="showAllCriteria" max-width="800" scrollable>
      <v-card>
        <v-card-title class="dialog-header" style="background: linear-gradient(135deg, #00479b, #0066d6); color: white;">
          <v-icon left color="white">assessment</v-icon>
          All eligibility criteria
          <v-spacer></v-spacer>
          <v-btn icon dark @click="showAllCriteria = false"><v-icon>close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="pa-6">
          <v-alert type="info" border="left" class="mb-6">
            <v-icon small left color="info">info</v-icon>
            You only need to meet <strong>ONE</strong> criterion to be eligible.
          </v-alert>

          <!-- Critère 1: Completed Campaigns -->
          <v-card outlined class="mb-4">
            <v-card-text class="pa-4">
              <div class="criterion-detail-header">
                <v-icon :color="allCriteria[0].met ? 'success' : 'grey'" class="mr-2">
                  {{ allCriteria[0].met ? 'check_circle' : 'cancel' }}
                </v-icon>
                <div class="criterion-detail-title">{{ allCriteria[0].name }}</div>
              </div>

              <div class="criterion-detail-status" :class="{ 'status-success': allCriteria[0].met, 'status-failed': !allCriteria[0].met }">
                {{ allCriteria[0].statusText }}
              </div>

              <div class="criterion-detail-desc">{{ allCriteria[0].description }}</div>

              <!-- Détails si disponibles -->
              <div v-if="eligibility.details.completed_campaigns" class="criterion-extra-details mt-3">
                <v-divider class="mb-3"></v-divider>
                <div class="detail-item">
                  <v-icon small color="#00479b" left>account_balance_wallet</v-icon>
                  <strong>Your total saved:</strong> {{ formatAmount(eligibility.details.completed_campaigns.total_saved) }} FCFA
                </div>
                <div class="detail-item">
                  <v-icon small color="success" left>credit_card</v-icon>
                  <strong>Max loan available:</strong> {{ formatAmount(eligibility.details.completed_campaigns.max_loan) }} FCFA
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Critère 2: Active Investment -->
          <v-card outlined class="mb-4">
            <v-card-text class="pa-4">
              <div class="criterion-detail-header">
                <v-icon :color="allCriteria[1].met ? 'success' : 'grey'" class="mr-2">
                  {{ allCriteria[1].met ? 'check_circle' : 'cancel' }}
                </v-icon>
                <div class="criterion-detail-title">{{ allCriteria[1].name }}</div>
              </div>

              <div class="criterion-detail-status" :class="{ 'status-success': allCriteria[1].met, 'status-failed': !allCriteria[1].met }">
                {{ allCriteria[1].statusText }}
              </div>

              <div class="criterion-detail-desc">{{ allCriteria[1].description }}</div>

              <!-- Détails si disponibles -->
              <div v-if="eligibility.details.active_investment" class="criterion-extra-details mt-3">
                <v-divider class="mb-3"></v-divider>
                <div class="detail-item">
                  <v-icon small color="#00479b" left>trending_up</v-icon>
                  <strong>Amount already paid:</strong> {{ formatAmount(eligibility.details.active_investment.total_invested) }} FCFA
                </div>
                <div class="detail-item">
                  <v-icon small color="success" left>credit_card</v-icon>
                  <strong>Max loan available:</strong> {{ formatAmount(eligibility.details.active_investment.max_loan) }} FCFA
                </div>
                <div class="detail-item-note">
                  <v-icon x-small color="info" left>info</v-icon>
                  {{ eligibility.details.active_investment.calculation }}
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Critère 3: Business Affiliates -->
          <v-card outlined class="mb-4">
            <v-card-text class="pa-4">
              <div class="criterion-detail-header">
                <v-icon :color="allCriteria[2].met ? 'success' : 'grey'" class="mr-2">
                  {{ allCriteria[2].met ? 'check_circle' : 'cancel' }}
                </v-icon>
                <div class="criterion-detail-title">{{ allCriteria[2].name }}</div>
              </div>

              <div class="criterion-detail-status" :class="{ 'status-success': allCriteria[2].met, 'status-failed': !allCriteria[2].met }">
                {{ allCriteria[2].statusText }}
              </div>

              <div class="criterion-detail-desc">{{ allCriteria[2].description }}</div>

              <!-- Détails si disponibles -->
              <div v-if="eligibility.details.business_affiliates" class="criterion-extra-details mt-3">
                <v-divider class="mb-3"></v-divider>
                <div class="detail-item">
                  <v-icon small color="#00479b" left>people</v-icon>
                  <strong>Your affiliates count:</strong> {{ eligibility.details.business_affiliates.affiliate_count }}
                </div>
                <div class="detail-item">
                  <v-icon small color="success" left>credit_card</v-icon>
                  <strong>Max loan available:</strong> {{ formatAmount(eligibility.details.business_affiliates.max_loan) }} FCFA
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Critère 4: Monthly Njangi Group -->
          <v-card outlined class="mb-4">
            <v-card-text class="pa-4">
              <div class="criterion-detail-header">
                <v-icon :color="allCriteria[3].met ? 'success' : 'grey'" class="mr-2">
                  {{ allCriteria[3].met ? 'check_circle' : 'cancel' }}
                </v-icon>
                <div class="criterion-detail-title">{{ allCriteria[3].name }}</div>
              </div>

              <div class="criterion-detail-status" :class="{ 'status-success': allCriteria[3].met, 'status-failed': !allCriteria[3].met }">
                {{ allCriteria[3].statusText }}
              </div>

              <div class="criterion-detail-desc">{{ allCriteria[3].description }}</div>

              <!-- Détails si disponibles (peut avoir plusieurs groupes) -->
              <div v-for="(detail, key) in eligibility.details" :key="key" class="criterion-extra-details mt-3">
                <div v-if="key.includes('monthly_group')">
                  <v-divider class="mb-3"></v-divider>
                  <div class="njangi-group-name">
                    <v-icon small color="#00479b" left>group</v-icon>
                    <strong>{{ detail.group_name }}</strong>
                  </div>
                  <div class="detail-item">
                    <v-icon x-small left>people</v-icon>
                    <strong>Members:</strong> {{ detail.num_members }}
                  </div>
                  <div class="detail-item">
                    <v-icon x-small left>payment</v-icon>
                    <strong>Your contribution:</strong> {{ formatAmount(detail.contribution_amount) }} FCFA
                  </div>
                  <div class="detail-item">
                    <v-icon x-small left>account_balance</v-icon>
                    <strong>You will receive:</strong> {{ formatAmount(detail.expected_personal_collection) }} FCFA
                  </div>
                  <div class="detail-item">
                    <v-icon x-small left>my_location</v-icon>
                    <strong>Your position:</strong> {{ detail.position }} (Current: {{ detail.current_position }})
                  </div>
                  <div class="detail-item">
                    <v-icon small color="success" left>credit_card</v-icon>
                    <strong>Max loan available:</strong> {{ formatAmount(detail.max_loan) }} FCFA
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>

        </v-card-text>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn color="#00479b" dark @click="showAllCriteria = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══════════════ TERMS DIALOG ══════════════ -->
    <v-dialog v-model="showTermsDialog" max-width="700" scrollable>
      <v-card>
        <v-card-title class="dialog-header" style="background: linear-gradient(135deg, #00479b, #0066d6); color: white;">
          Terms of use
          <v-spacer></v-spacer>
          <v-btn icon dark @click="showTermsDialog = false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-8">
          <h3 class="mb-4">Loan terms</h3>
          <ol class="terms-list mb-6">
            <li>Loans range from 5,000 to 50,000 FCFA in increments of 5,000 FCFA.</li>
            <li>The repayment period is 15 or 25 days (interest is fixed at application).</li>
            <li>Full repayment (principal + interest) must be made by the agreed date.</li>
            <li>Non-payment may incur additional fees and affect future eligibility.</li>
            <li>Only one active loan is allowed at a time.</li>
            <li>Disbursement will be made to the Mobile Money number provided.</li>
          </ol>

          <h3 class="mb-4">Your responsibilities</h3>
          <ul class="terms-list">
            <li>Provide accurate personal and financial information.</li>
            <li>Ensure the Mobile Money number belongs to you.</li>
            <li>Repay before or on the due date.</li>
            <li>Immediately notify us of any change in your situation.</li>
          </ul>
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn color="#00479b" dark large @click="agreeToTerms = true; showTermsDialog = false">
            <v-icon left>check_circle</v-icon>
            Accept and close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══════════════ SUCCESS DIALOG ══════════════ -->
    <v-dialog v-model="successDialog" max-width="600" persistent>
      <v-card class="success-dialog">
        <div class="success-header">
          <div class="success-icon-big">
            <v-icon color="white" size="96">check_circle</v-icon>
          </div>
          <h2 class="success-title">Application submitted!</h2>
          <p class="success-subtitle">Your loan is being reviewed</p>
        </div>

        <v-card-text class="pa-8">
          <v-card class="success-details-card">
            <v-card-text>
              <div class="detail-row">
                <span>Loan amount</span>
                <span class="font-weight-bold">{{ formatAmount(submittedLoan.loan_amount) }} FCFA</span>
              </div>
              <div class="detail-row">
                <span>Interest</span>
                <span class="font-weight-bold orange--text">{{ formatAmount(submittedLoan.interest_amount) }} FCFA</span>
              </div>
              <div class="detail-row">
                <span>Total to repay</span>
                <span class="font-weight-bold" style="color: #00479b; font-size: 1.2rem">
                  {{ formatAmount(submittedLoan.total_amount) }} FCFA
                </span>
              </div>
              <div class="detail-row">
                <span>Due date</span>
                <span class="font-weight-bold">{{ submittedLoan.due_date }}</span>
              </div>
              <div class="detail-row">
                <span>File #</span>
                <span class="font-weight-bold">#{{ submittedLoan.id }}</span>
              </div>
              <div class="detail-row">
                <span>Status</span>
                <v-chip small color="orange" text-color="white">
                  <v-icon left x-small>schedule</v-icon>
                  Pending review
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <v-alert type="info" border="left" class="mt-6">
            <v-icon small left>notifications_active</v-icon>
            You will receive a notification once your application is reviewed.
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn text @click="goToLoans">
            <v-icon left>list</v-icon>
            View loans
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import axios from 'axios'

const INTEREST_STRUCTURE = {
  5000:  { 15: 325,  25: 575  },
  10000: { 15: 500,  25: 750  },
  15000: { 15: 750,  25: 1000 },
  20000: { 15: 1000, 25: 1500 },
  25000: { 15: 1250, 25: 1750 },
  30000: { 15: 1500, 25: 2000 },
  35000: { 15: 1750, 25: 2500 },
  40000: { 15: 2000, 25: 3000 },
  45000: { 15: 2250, 25: 3250 },
  50000: { 15: 2500, 25: 3500 },
}

export default {
  name: 'LoanApplicationStepper',

  data() {
    return {
      loading: true,
      submitting: false,
      currentStep: 1,
      agreeToTerms: false,
      showTermsDialog: false,
      showAllCriteria: false,
      showEligibilityExplanation: false,
      successDialog: false,

      // Animation states
      currentCheckStep: 0,
      checkSteps: [
        'Checking campaigns...',
        'Verifying investments...',
        'Counting affiliates...',
        'Checking Njangi groups...',
        'Finalizing eligibility...'
      ],

      stepNames: ['Amount', 'Period', 'Payment', 'Purpose', 'Confirm'],

      eligibility: {
        is_eligible: false,
        max_amount: 0,
        criteria_met: [],
        details: {},
        has_active_loan: false,
        active_loan_warning: null,
        active_loan_details: null
      },

      loanApplication: {
        loan_amount: null,
        repayment_period_days: 15,
        phone_number: '',
        operator: null,
        purpose: ''
      },

      submittedLoan: {},

      phoneRules: [
        v => !!v || 'Phone number is required',
        v => v.length >= 9 || 'At least 9 digits'
      ],

      purposeRules: [
        v => !v || v.length <= 500 || 'Limited to 500 characters'
      ],

      termsRules: [
        v => !!v || 'Acceptance required'
      ]
    }
  },

  computed: {
    availableAmounts() {
      const amounts = []
      const max = this.eligibility.max_amount
      for (let a = 5000; a <= max; a += 5000) amounts.push(a)
      return amounts
    },

    currentInterest() {
      if (!this.loanApplication.loan_amount) return 0
      return this.getInterest(this.loanApplication.repayment_period_days)
    },

    totalRepayment() {
      return (this.loanApplication.loan_amount || 0) + this.currentInterest
    },

    dueDateDisplay() {
      if (!this.loanApplication.repayment_period_days) return '—'
      const d = new Date()
      d.setDate(d.getDate() + this.loanApplication.repayment_period_days)
      return d.toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' })
    },

    interestRatePct() {
      const a = this.loanApplication.loan_amount
      if (!a) return '—'
      return ((this.currentInterest / a) * 100).toFixed(2) + '%'
    },

    progressPercent() {
      return (this.currentStep / 5) * 100
    },

    allCriteria() {
      return [
        {
          name: 'Completed Campaigns',
          met: this.eligibility.criteria_met.includes('completed_campaigns'),
          statusText: this.eligibility.criteria_met.includes('completed_campaigns')
            ? '✓ You have completed campaigns totaling ≥ 500,000 FCFA'
            : '✗ You need to complete campaigns totaling 500,000 FCFA',
          description: 'Complete savings campaigns totaling at least 500,000 FCFA to unlock up to 25,000 FCFA in loans.'
        },
        {
          name: 'Active Investment',
          met: this.eligibility.criteria_met.includes('active_investment'),
          statusText: this.eligibility.criteria_met.includes('active_investment')
            ? '✓ You have active investments'
            : '✗ No active investments found',
          description: 'Have an active investment to borrow an amount less than or equal to your invested amount (max 50,000 FCFA).'
        },
        {
          name: 'Business Affiliates',
          met: this.eligibility.criteria_met.includes('business_affiliates'),
          statusText: this.eligibility.criteria_met.includes('business_affiliates')
            ? '✓ You have 10+ affiliates'
            : '✗ You need at least 10 affiliates',
          description: 'Refer at least 10 active affiliates to unlock up to 50,000 FCFA in loans.'
        },
        {
          name: 'Monthly Njangi Group',
          met: this.eligibility.criteria_met.some(c => c.includes('monthly_group')),
          statusText: this.eligibility.criteria_met.some(c => c.includes('monthly_group'))
            ? '✓ You are an active Njangi member with no unpaid penalties (position not yet reached)'
            : '✗ You are not eligible: your turn may have passed, you have unpaid penalties, or you are not in an active group',
          description: 'Be an active member of a monthly Njangi group, have no unpaid penalties, and have not yet received your turn to borrow up to your expected collection (max 50,000 FCFA).'
        }
      ]
    }
  },

async mounted() {
  this.$store.commit('setIsLoading', true)

  try {
    // Faire la vraie requête immédiatement
    const response = await axios.get('/api/v1/savings/loans/eligibility/')
    const data = response.data

    if (data.has_active_loan) {
      // Si prêt actif, on affiche directement sans animation
      this.eligibility = data
      this.loading = false
      this.$store.commit('setIsLoading', false)
      return
    }

    // Sinon, on lance l'animation de vérification
    this.loading = true
    for (let i = 0; i < this.checkSteps.length; i++) {
      this.currentCheckStep = i
      await this.delay(600)
    }
    // À la fin de l'animation, on affecte les données
    this.eligibility = data
    this.loading = false

  } catch (error) {
    console.error('Error:', error)
    this.$store.commit('setSnackBarMessage', 'Verification error')
    this.$store.commit('setSnackBarColor', 'red darken-4')
    this.$store.commit('activateSnackBar', true)
    this.loading = false
  } finally {
    this.$store.commit('setIsLoading', false)
  }
},

  methods: {
    async checkEligibilityWithAnimation() {
      this.loading = true

      // Simulate step-by-step animation
      for (let i = 0; i < this.checkSteps.length; i++) {
        this.currentCheckStep = i
        await this.delay(600)
      }

      // Fetch real eligibility
      try {
        const response = await axios.get('/api/v1/savings/loans/eligibility/')
        this.eligibility = response.data
      } catch (error) {
        console.error('Error:', error)
        this.$store.commit('setSnackBarMessage', 'Verification error')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      } finally {
        await this.delay(400)
        this.loading = false
      }
    },

    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    nextStep() {
      if (this.currentStep < 5) {
        this.currentStep++
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },

    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },

    jumpToStep(step) {
      if (step < this.currentStep) {
        this.currentStep = step
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },

    selectAmount(amount) {
      this.loanApplication.loan_amount = amount
      if (navigator.vibrate) navigator.vibrate(10)
    },

    selectPeriod(days) {
      this.loanApplication.repayment_period_days = days
      if (navigator.vibrate) navigator.vibrate(10)
    },

    selectOperator(op) {
      this.loanApplication.operator = op
      if (navigator.vibrate) navigator.vibrate(10)
    },

    getInterest(days) {
      const a = this.loanApplication.loan_amount
      if (!a || !INTEREST_STRUCTURE[a]) return 0
      return INTEREST_STRUCTURE[a][days] || 0
    },

    async submitApplication() {
      this.submitting = true
      try {
        const payload = {
          loan_amount: this.loanApplication.loan_amount,
          repayment_period_days: this.loanApplication.repayment_period_days,
          phone_number: this.loanApplication.phone_number,
          operator: this.loanApplication.operator,
          purpose: this.loanApplication.purpose
        }
        const response = await axios.post('/api/v1/savings/loans/apply/', payload)
        if (response.data.success) {
          this.submittedLoan = response.data.loan
          this.successDialog = true
        }
      } catch (error) {
        const msg = error.response?.data?.error || 'Submission error'
        this.$store.commit('setSnackBarMessage', msg)
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      } finally {
        this.submitting = false
      }
    },

    resetForm() {
      this.currentStep = 1
      this.loanApplication = {
        loan_amount: null,
        repayment_period_days: 15,
        phone_number: '',
        operator: null,
        purpose: ''
      }
      this.agreeToTerms = false
      this.checkEligibilityWithAnimation()
    },

    goToLoans() {
      this.$router.push('/savings/loans/active')
    },

    formatAmount(amount) {
      if (!amount && amount !== 0) return '—'
      return new Intl.NumberFormat('en-US').format(amount)
    },

    formatCriterion(key) {
      const labels = {
        completed_campaigns: 'Campaigns',
        active_investment: 'Investments',
        business_affiliates: 'Affiliates',
        monthly_group: 'Njangi'
      }
      if (key.includes('monthly_group')) return 'Njangi'
      return labels[key] || key
    },

    showCriterionDetail(idx) {
      this.showAllCriteria = true
    },

    getRepaymentProgress() {
      if (!this.eligibility.active_loan_details) return 0
      const paid = this.eligibility.active_loan_details.amount_paid
      const total = this.eligibility.active_loan_details.total_amount
      if (total === 0) return 0
      return Math.round((paid / total) * 100)
    },

    getStatusColor(status) {
      const colors = {
        'Pending': 'orange',
        'Approved': 'success',
        'Disbursed': 'primary',
        'Repaying': 'purple',
        'Ongoing': 'primary'
      }
      return colors[status] || 'grey'
    },

    formatDateLoan(dateStr) {
      if (!dateStr) return '—'
      const d = new Date(dateStr)
      return d.toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' })
    }
  }
}
</script>

<style scoped>
/* ══════════════ BASE STYLES ══════════════ */
.loan-stepper-app {
  position: relative;
  max-width: 1100px;
  margin: 0 auto;
  padding: 40px 20px;
}

/* Cosmic BG */
.cosmic-bg {
  position: fixed;
  inset: 0;
  z-index: -1;
  background: linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%);
}

.cosmic-orb {
  position: absolute;
  border-radius: 50%;
  opacity: 0.04;
  filter: blur(100px);
  animation: float 20s infinite ease-in-out;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #00479b, #0066d6);
  top: -150px;
  left: -150px;
}

.orb-2 {
  width: 350px;
  height: 350px;
  background: linear-gradient(135deg, #ff9800, #f57c00);
  bottom: -100px;
  right: -100px;
  animation-delay: 7s;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #1b5e20, #4caf50);
  top: 50%;
  right: -50px;
  animation-delay: 14s;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(30px, 30px); }
}

/* ══════════════ LOADING ANIMATION ══════════════ */
.loading-card {
  border-radius: 16px !important;
  padding: 80px 40px !important;
  background: white !important;
}

.loading-content {
  text-align: center;
}

.eligibility-animation-wrapper {
  position: relative;
  width: 180px;
  height: 180px;
  margin: 0 auto;
}

.scanning-circle {
  position: absolute;
  inset: 0;
  border: 4px solid #e0e0e0;
  border-radius: 50%;
}

.scan-line {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 90%;
  height: 4px;
  background: linear-gradient(90deg, transparent, #00479b, transparent);
  transform: translate(-50%, -50%);
  animation: scan 2s linear infinite;
}

@keyframes scan {
  0% { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
}

.check-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.loading-title {
  font-size: 1.6rem;
  font-weight: 800;
  color: #212121;
}

.loading-subtitle {
  color: #999;
  margin-top: 8px;
}

.check-steps {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 400px;
  margin: 0 auto;
}

.check-step {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  border-radius: 8px;
  background: #f8f9fc;
  transition: all 0.3s ease;
}

.check-step.step-active {
  background: rgba(0, 71, 155, 0.05);
  border-left: 4px solid #00479b;
}

.check-step.step-complete {
  background: rgba(27, 94, 32, 0.05);
  border-left: 4px solid #1b5e20;
}

.step-icon-wrapper {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-spinner {
  width: 20px;
  height: 20px;
  border: 3px solid #e0e0e0;
  border-top-color: #00479b;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.step-text {
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

/* ══════════════ NOT ELIGIBLE STATE ══════════════ */
.not-eligible-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.not-eligible-header {
  background: linear-gradient(135deg, #ff9800, #f57c00);
  padding: 60px 40px;
  text-align: center;
}

.not-eligible-title {
  font-size: 2rem;
  font-weight: 900;
  color: #212121;
  margin-top: 24px;
}

.not-eligible-desc {
  color: #666;
  margin-top: 12px;
  line-height: 1.6;
}

.criteria-status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.criterion-status-card {
  position: relative;
  padding: 24px !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.criterion-status-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.criterion-met {
  border-color: #1b5e20 !important;
  background: rgba(27, 94, 32, 0.03) !important;
}

.criterion-unmet {
  border-color: #e0e0e0 !important;
}

.criterion-icon-wrapper {
  flex-shrink: 0;
}

.criterion-content {
  flex-grow: 1;
}

.criterion-name {
  font-weight: 700;
  color: #212121;
  margin-bottom: 8px;
}

.criterion-status-text {
  font-size: 0.85rem;
  color: #666;
  line-height: 1.5;
}

.criterion-status-text.text-success {
  color: #1b5e20;
}

.criterion-info-btn {
  flex-shrink: 0;
}

/* ══════════════ ACTIVE LOAN BLOCK STATE ══════════════ */
.active-loan-block-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.active-loan-header {
  background: linear-gradient(135deg, #ff9800, #f57c00);
  padding: 60px 40px;
  text-align: center;
}

.active-loan-title {
  font-size: 2rem;
  font-weight: 900;
  color: #212121;
  margin-top: 24px;
}

.active-loan-desc {
  color: #666;
  margin-top: 12px;
  line-height: 1.6;
  font-size: 1rem;
}

.active-loan-details {
  border-radius: 12px !important;
}

.loan-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e0e0e0;
}

.loan-detail-row:last-child {
  border-bottom: none;
}

.loan-detail-row.highlight {
  background: rgba(255, 152, 0, 0.05);
  padding: 16px 12px;
  margin: 8px -12px;
  border-radius: 8px;
  border: none;
}

.progress-label {
  font-weight: 600;
  color: #666;
  font-size: 0.9rem;
}

.future-eligibility-card {
  border-radius: 12px !important;
}

.eligibility-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* ══════════════ SUCCESS BANNER ══════════════ */
.success-banner {
  background: linear-gradient(135deg, #e8f5e9, #f1f8e9) !important;
  border: 2px solid #1b5e20 !important;
  border-radius: 16px !important;
}

.success-banner-content {
  display: flex;
  align-items: center;
  gap: 24px;
}

.success-icon-wrapper {
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #1b5e20, #2e7d32);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: scale-pop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes scale-pop {
  0% { transform: scale(0); }
  100% { transform: scale(1); }
}

.success-title {
  font-size: 1.6rem;
  font-weight: 900;
  color: #1b5e20;
}

.success-subtitle {
  color: #666;
  margin-top: 4px;
}

.met-criteria {
  margin-top: 24px;
}

.criteria-label {
  font-weight: 700;
  color: #1b5e20;
  margin-bottom: 12px;
}

.criteria-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* ══════════════ MOBILE CUSTOM STEPPER ══════════════ */
.mobile-stepper-wrapper {
  max-width: 600px;
  margin: 0 auto;
}

.mobile-progress-container {
  margin-bottom: 24px;
}

.mobile-progress-bar {
  height: 6px;
  background: #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 8px;
}

.mobile-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00479b, #0066d6);
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-step-indicator {
  text-align: center;
  font-size: 0.85rem;
  color: #999;
  font-weight: 600;
}

.mobile-steps-dots {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
}

.mobile-dot {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.95rem;
  color: #666;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-dot.dot-active {
  background: linear-gradient(135deg, #00479b, #0066d6);
  color: white;
  transform: scale(1.2);
  box-shadow: 0 4px 12px rgba(0, 71, 155, 0.4);
}

.mobile-dot.dot-completed {
  background: #1b5e20;
  color: white;
}

.mobile-step-name {
  text-align: center;
  font-size: 1.4rem;
  font-weight: 800;
  color: #212121;
  margin-bottom: 24px;
}

.mobile-step-card {
  border-radius: 16px !important;
  padding: 24px;
  min-height: 500px;
}

.mobile-step-content {
  display: flex;
  flex-direction: column;
  min-height: 450px;
}

.mobile-step-header {
  text-align: center;
  margin-bottom: 32px;
}

.mobile-step-header h3 {
  font-size: 1.4rem;
  font-weight: 800;
  color: #212121;
  margin-top: 12px;
}

.mobile-step-subtitle {
  color: #999;
  font-size: 0.9rem;
  margin-top: 4px;
}

/* ══════════════ AMOUNTS GRID ══════════════ */
.mobile-amounts-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;
  flex-grow: 1;
}

.mobile-amount-card {
  position: relative;
  padding: 24px 12px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.mobile-amount-card:active {
  transform: scale(0.95);
}

.mobile-amount-selected {
  border-color: #00479b;
  background: rgba(0, 71, 155, 0.05);
  box-shadow: 0 0 0 3px rgba(0, 71, 155, 0.1);
}

.mobile-amount-value {
  font-size: 1.3rem;
  font-weight: 900;
  color: #00479b;
}

.mobile-amount-unit {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
  font-weight: 600;
}

.mobile-amount-check {
  position: absolute;
  top: 8px;
  right: 8px;
}

/* ══════════════ PERIOD OPTIONS ══════════════ */
.mobile-period-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
  flex-grow: 1;
}

.mobile-period-card {
  position: relative;
  padding: 28px 24px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
}

.mobile-period-card:active {
  transform: scale(0.98);
}

.mobile-period-selected {
  border-color: #00479b;
  background: rgba(0, 71, 155, 0.05);
  box-shadow: 0 0 0 3px rgba(0, 71, 155, 0.1);
}

.mobile-period-days {
  font-size: 1.4rem;
  font-weight: 900;
  color: #212121;
  margin: 12px 0 4px;
}

.mobile-period-label {
  color: #999;
  font-size: 0.85rem;
  margin-bottom: 12px;
  font-weight: 500;
}

.mobile-period-interest {
  font-weight: 700;
  color: #ff9800;
  margin-top: 12px;
  font-size: 0.95rem;
}

.mobile-period-check {
  position: absolute;
  top: 12px;
  right: 12px;
}

/* ══════════════ OPERATOR OPTIONS ══════════════ */
.mobile-operator-label {
  font-weight: 700;
  color: #212121;
  margin-bottom: 16px;
  font-size: 1rem;
}

.mobile-operator-options {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}

.mobile-operator-card {
  position: relative;
  flex: 1;
  padding: 24px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
}

.mobile-operator-card:active {
  transform: scale(0.95);
}

.mobile-operator-selected {
  border-color: #00479b;
  background: rgba(0, 71, 155, 0.05);
  box-shadow: 0 0 0 3px rgba(0, 71, 155, 0.1);
}

.mobile-operator-name {
  font-weight: 700;
  color: #212121;
  margin-top: 12px;
  font-size: 0.95rem;
}

.mobile-operator-check {
  position: absolute;
  top: 8px;
  right: 8px;
}

/* ══════════════ SUMMARY ══════════════ */
.mobile-summary {
  background: linear-gradient(135deg, #f8f9fc, #f1f3f8);
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 16px;
}

.mobile-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e0e0e0;
}

.mobile-summary-row:last-child {
  border-bottom: none;
}

.mobile-summary-row.total {
  padding-top: 16px;
  margin-top: 8px;
  border-top: 2px solid #00479b;
}

.mobile-summary-row span {
  font-size: 0.9rem;
  color: #666;
}

.mobile-summary-row .value {
  font-weight: 700;
  color: #212121;
  font-size: 1rem;
}

.mobile-summary-row.total .value {
  font-size: 1.4rem;
  color: #00479b;
}

/* ══════════════ STEP ACTIONS ══════════════ */
.mobile-step-actions {
  display: flex;
  gap: 12px;
  margin-top: auto;
  padding-top: 16px;
}

.mobile-back-btn,
.mobile-next-btn,
.mobile-submit-btn {
  flex: 1;
  font-weight: 700 !important;
}

.mobile-next-btn {
  margin-top: auto;
}

.mobile-purpose-textarea {
  flex-grow: 1;
}

.mobile-terms-checkbox {
  margin-top: 16px !important;
  margin-bottom: 16px !important;
}

.mobile-terms-text {
  font-size: 0.9rem;
  color: #666;
}

.terms-link {
  color: #00479b;
  font-weight: 700;
  text-decoration: none;
}

.terms-link:hover {
  text-decoration: underline;
}

/* ══════════════ DIALOGS ══════════════ */
.dialog-header {
  font-weight: 800 !important;
  font-size: 1.2rem !important;
}

.criterion-detail-card {
  animation: fade-in 0.4s ease-out;
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.criterion-detail-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.criterion-detail-title {
  font-weight: 700;
  color: #212121;
  font-size: 1.1rem;
}

.criterion-detail-status {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  display: inline-block;
}

.status-success {
  background: rgba(27, 94, 32, 0.1);
  color: #1b5e20;
}

.status-failed {
  background: rgba(158, 158, 158, 0.1);
  color: #666;
}

.criterion-detail-desc {
  font-size: 0.85rem;
  color: #666;
  line-height: 1.6;
  margin-top: 8px;
}

/* ══════════════ SUCCESS DIALOG ══════════════ */
.success-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.success-header {
  background: linear-gradient(135deg, #00479b, #0066d6);
  padding: 48px 32px;
  text-align: center;
  color: white;
}

.success-icon-big {
  animation: bounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes bounce {
  0% { transform: scale(0); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.success-details-card {
  background: linear-gradient(135deg, #f8f9fc, #f1f3f8) !important;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #e0e0e0;
}

.detail-row:last-child {
  border-bottom: none;
}

.terms-list {
  line-height: 1.8;
}

.terms-list li {
  margin-bottom: 12px;
}

/* ══════════════ CRITERION EXTRA DETAILS ══════════════ */
.criterion-extra-details {
  background: linear-gradient(135deg, #f8f9fc, #f1f3f8);
  padding: 16px;
  border-radius: 8px;
}

.detail-item {
  display: flex;
  align-items: center;
  padding: 6px 0;
  font-size: 0.9rem;
  color: #212121;
}

.detail-item strong {
  margin-right: 8px;
}

.detail-item-note {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  margin-top: 8px;
  background: rgba(0, 71, 155, 0.05);
  border-radius: 6px;
  font-size: 0.85rem;
  color: #00479b;
  font-style: italic;
}

.njangi-group-name {
  display: flex;
  align-items: center;
  font-size: 1rem;
  color: #00479b;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #00479b;
}

/* ══════════════ RESPONSIVE ══════════════ */
@media (max-width: 600px) {
  .loan-stepper-app {
    padding: 20px 12px !important;
  }

  .loading-card {
    padding: 40px 20px !important;
  }

  .eligibility-animation-wrapper {
    width: 140px !important;
    height: 140px !important;
  }

  .check-icon {
    font-size: 48px !important;
  }

  .loading-title {
    font-size: 1.3rem !important;
  }

  .not-eligible-header,
  .active-loan-header {
    padding: 40px 24px !important;
  }

  .not-eligible-title,
  .active-loan-title {
    font-size: 1.5rem !important;
  }

  .criteria-status-grid {
    grid-template-columns: 1fr !important;
  }

  .success-banner-content {
    flex-direction: column !important;
    text-align: center !important;
  }

  .success-icon-wrapper {
    width: 56px !important;
    height: 56px !important;
  }

  .success-title {
    font-size: 1.3rem !important;
  }

  .met-criteria {
    text-align: center;
  }

  .criteria-chips {
    justify-content: center;
  }

  .mobile-step-card {
    padding: 20px !important;
  }

  .mobile-step-header h3 {
    font-size: 1.2rem !important;
  }

  .mobile-amounts-grid {
    gap: 10px !important;
  }

  .mobile-amount-card {
    padding: 20px 10px !important;
  }

  .mobile-amount-value {
    font-size: 1.1rem !important;
  }

  .mobile-dot {
    width: 36px !important;
    height: 36px !important;
    font-size: 0.85rem !important;
  }

  .success-header {
    padding: 32px 20px !important;
  }
}

@media (max-width: 400px) {
  .mobile-amounts-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }

  .mobile-amount-value {
    font-size: 1rem !important;
  }

  .mobile-step-name {
    font-size: 1.2rem !important;
  }
}
</style>